import { LocalCodingAgentRuntime } from '@this+that/models';
import { StateCreator } from 'zustand';

import {
  handleClearBandFocus,
  handleClearEntry,
  handleConsumeDraft,
  handleHandOffToPanel,
  handlePresetDelegateTarget,
  handleRecordAgentTarget,
  handleRequestBandFocus,
  handleRequestDelegate,
  handleRequestFocus,
  handleSetLocalAgentProfile,
  handleSetLocalModel,
  handleSetLocalRuntime,
  handleSetPlanFirst,
  handleSetTarget,
} from './handlers';
import {
  DelegatePanelHandOff,
  DoBoxDelegateComposerState,
  DoBoxDelegateComposerStore,
  DoBoxDelegateComposerTarget,
  HandlerSet,
} from './types';
import { useFloatingAssistantStore } from '../FloatingAssistant';

export const INITIAL_DOBOX_DELEGATE_COMPOSER_STATE: DoBoxDelegateComposerState =
  {
    stateByEntryId: {},
    lastAgentByEntryId: {},
    lastAgent: null,
    pendingDraftByEntryId: {},
    bandFocusEntryId: null,
  };

export const createDoBoxDelegateComposerSlice: StateCreator<
  DoBoxDelegateComposerStore
> = (set, get) => {
  const handlerSet: HandlerSet = (updater) => {
    if (typeof updater === 'function') {
      set(
        (fullState) =>
          updater(fullState) as Partial<DoBoxDelegateComposerStore>,
      );
    } else {
      set(updater as Partial<DoBoxDelegateComposerStore>);
    }
  };

  return {
    ...INITIAL_DOBOX_DELEGATE_COMPOSER_STATE,

    requestDelegate: (
      entryId: string,
      target?: DoBoxDelegateComposerTarget,
    ) => {
      handleRequestDelegate(handlerSet, entryId, target);
      useFloatingAssistantStore.getState().requestHostedPanelOpen(entryId);
    },
    handOffToPanel: (entryId: string, handOff: DelegatePanelHandOff) => {
      handleHandOffToPanel(handlerSet, entryId, handOff);
      useFloatingAssistantStore.getState().requestHostedPanelOpen(entryId);
    },
    consumeDraft: (entryId: string) =>
      handleConsumeDraft(handlerSet, get().pendingDraftByEntryId, entryId),
    requestBandFocus: (entryId: string) =>
      handleRequestBandFocus(handlerSet, entryId),
    clearBandFocus: (entryId: string) =>
      handleClearBandFocus(handlerSet, entryId),
    presetDelegateTarget: (
      entryId: string,
      target: DoBoxDelegateComposerTarget,
      planFirst = true,
    ) => handlePresetDelegateTarget(handlerSet, entryId, target, planFirst),
    setTarget: (entryId: string, target: DoBoxDelegateComposerTarget) =>
      handleSetTarget(handlerSet, entryId, target),
    setPlanFirst: (entryId: string, planFirst: boolean) =>
      handleSetPlanFirst(handlerSet, entryId, planFirst),
    setLocalRuntime: (entryId: string, runtime: LocalCodingAgentRuntime) =>
      handleSetLocalRuntime(handlerSet, entryId, runtime),
    setLocalModel: (entryId: string, model: string) =>
      handleSetLocalModel(handlerSet, entryId, model),
    setLocalAgentProfile: (entryId: string, profile: string) =>
      handleSetLocalAgentProfile(handlerSet, entryId, profile),
    recordAgentTarget: (entryId: string, target: DoBoxDelegateComposerTarget) =>
      handleRecordAgentTarget(handlerSet, entryId, target),
    requestFocus: (entryId: string) => handleRequestFocus(handlerSet, entryId),
    clearEntry: (entryId: string) => handleClearEntry(handlerSet, entryId),
    reset: () => set(INITIAL_DOBOX_DELEGATE_COMPOSER_STATE),
  };
};
